import { useState } from "react";
import { Mail, Phone, Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FadeUp, SectionHead } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const FALLBACK_SETTINGS = {
  email_eileen: "eileen@dfemalaysia.com",
  instagram: "@dfemalaysia",
  instagram_url: "https://instagram.com/dfemalaysia",
  facebook: "DREAM FACTORY ENTERTAINMENT",
  facebook_url: "https://www.facebook.com/dreamfactorypage",
};

const EVENT_TYPES = ["演唱会 / 音乐节", "艺人演出邀约", "商业发布活动", "夜店活动", "音乐制作", "其他合作"];
const BUDGETS = ["RM 50,000 以下", "RM 50,000 – 200,000", "RM 200,000 – 500,000", "RM 500,000 以上", "暂不确定"];

const EMPTY = { name: "", email: "", phone: "", company: "", event_type: EVENT_TYPES[0], budget: BUDGETS[0], message: "" };

export default function Contact() {
  const { lang } = useLang();
  const zh = lang === "zh";
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [loaded, setLoaded] = useState(false);

  if (!loaded) {
    setLoaded(true);
    api.get("/settings").then((r) => r.data && Object.keys(r.data).length && setSettings({ ...FALLBACK_SETTINGS, ...r.data })).catch(() => {});
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("已收到您的合作咨询，我们会尽快与您联系！");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <SectionHead no="05" en="BUSINESS ENQUIRY" zh="商业对接" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <FadeUp className="md:col-span-5">
            <p className="font-zh text-xl md:text-2xl text-[#f5f5f5] leading-relaxed">
              {zh ? (
                <>
                  无论是演唱会主办、艺人邀约还是品牌活动，
                  <span className="text-[#ffde00]">让梦想工厂为您的舞台开机。</span>
                </>
              ) : (
                <>
                  Concert promotion, artist booking or brand events —{" "}
                  <span className="text-[#ffde00]">let the Dream Factory power your stage.</span>
                </>
              )}
            </p>
            <div className="mt-10 space-y-6" data-testid="contact-info">
              <div className="flex items-start gap-4">
                <Mail size={20} className="text-[#ffde00] mt-1" />
                <div>
                  <div className="font-mono2 text-[10px] text-[#52525b] tracking-[0.3em]">EILEEN TAN</div>
                  <a href={`mailto:${settings.email_eileen}`} data-testid="contact-email-0" className="block text-[#f5f5f5] hover:text-[#ffde00] transition-colors mt-1">
                    {settings.email_eileen}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Instagram size={20} className="text-[#ffde00] mt-1" />
                <div>
                  <div className="font-mono2 text-[10px] text-[#52525b] tracking-[0.3em]">INSTAGRAM</div>
                  <a href={settings.instagram_url} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="block text-[#f5f5f5] hover:text-[#ffde00] transition-colors mt-1">
                    {settings.instagram}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Facebook size={20} className="text-[#ffde00] mt-1" />
                <div>
                  <div className="font-mono2 text-[10px] text-[#52525b] tracking-[0.3em]">FACEBOOK</div>
                  <a href={settings.facebook_url} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="block text-[#f5f5f5] hover:text-[#ffde00] transition-colors mt-1">
                    {settings.facebook}
                  </a>
                </div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15} className="md:col-span-7">
            <form onSubmit={submit} className="border border-white/10 bg-[#0a0a0a] p-6 md:p-10" data-testid="enquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "姓名 NAME" : "NAME"} *</label>
                  <input required className="dfe-input" data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder={zh ? "您的姓名" : "Your name"} />
                </div>
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "邮箱 EMAIL" : "EMAIL"} *</label>
                  <input required type="email" className="dfe-input" data-testid="enquiry-email" value={form.email} onChange={set("email")} placeholder="name@company.com" />
                </div>
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "电话 PHONE" : "PHONE"}</label>
                  <input className="dfe-input" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="+60 12-345 6789" />
                </div>
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "公司 COMPANY" : "COMPANY"}</label>
                  <input className="dfe-input" data-testid="enquiry-company" value={form.company} onChange={set("company")} placeholder={zh ? "公司 / 品牌名称" : "Company / brand"} />
                </div>
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "合作类型 TYPE" : "ENQUIRY TYPE"}</label>
                  <select className="dfe-input font-zh" data-testid="enquiry-type" value={form.event_type} onChange={set("event_type")}>
                    {EVENT_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "预算 BUDGET" : "BUDGET"}</label>
                  <select className="dfe-input font-zh" data-testid="enquiry-budget" value={form.budget} onChange={set("budget")}>
                    {BUDGETS.map((b) => (
                      <option key={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="font-mono2 text-[10px] text-[#a1a1aa] tracking-[0.3em] block mb-2">{zh ? "项目说明 MESSAGE" : "MESSAGE"}</label>
                  <textarea rows={5} className="dfe-input resize-none font-zh" data-testid="enquiry-message" value={form.message} onChange={set("message")} placeholder={zh ? "告诉我们您的演出想法：艺人、时间、地点、规模…" : "Tell us about your show: artists, dates, venue, scale…"} />
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="enquiry-submit" className="btn-yellow w-full justify-center mt-8 disabled:opacity-50">
                {busy ? (zh ? "发送中…" : "SENDING…") : zh ? "提交合作咨询" : "SEND ENQUIRY"}
                <Send size={15} />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
